import { useState, useCallback, useRef, useEffect } from 'react';
import type { PageContext } from './usePageContext';

const BASE_URL = import.meta.env.VITE_API_URL || '';
const STORAGE_KEY = 'rb-widget-state';

export type WidgetStatus = 'closed' | 'minimized' | 'open';

export interface WidgetMessage {
  id: number;
  role: string;
  content: string;
}

interface StoredState {
  status: WidgetStatus;
  conversationId: number | null;
}

function readStoredState(): StoredState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { status: 'closed', conversationId: null };
    const parsed = JSON.parse(raw);
    if (parsed.status === 'open' || parsed.status === 'minimized' || parsed.status === 'closed') {
      return { status: parsed.status, conversationId: parsed.conversationId ?? null };
    }
  } catch {
    // fall through to default
  }
  return { status: 'closed', conversationId: null };
}

function writeStoredState(state: StoredState): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function pageContextEquals(a: PageContext | null, b: PageContext | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.type === b.type && a.id === b.id;
}

export interface ChatWidgetState {
  status: WidgetStatus;
  conversationId: number | null;
  messages: WidgetMessage[];
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  open: () => void;
  minimize: () => void;
  close: () => void;
  sendMessage: (content: string) => Promise<void>;
}

export function useChatWidget(pageContext: PageContext | null): ChatWidgetState {
  const initial = useRef(readStoredState()).current;
  const [status, setStatus] = useState<WidgetStatus>(initial.status);
  const [conversationId, setConversationId] = useState<number | null>(initial.conversationId);
  const [messages, setMessages] = useState<WidgetMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const rehydratedRef = useRef(false);
  const startingConversationRef = useRef(false);
  // Tracks the pageContext that was last sent to the backend for this
  // conversation, so we only re-send it (triggering a new system-message
  // insertion server-side) when it has actually changed — not on every
  // message send. Compared by value (type+id), not object identity.
  const lastSentPageContextRef = useRef<PageContext | null>(null);

  const persist = useCallback((next: StoredState) => {
    writeStoredState(next);
  }, []);

  // Mount-time rehydration: only runs once, only if storage said the widget
  // was open/minimized with a real conversation to resume.
  useEffect(() => {
    if (rehydratedRef.current) return;
    rehydratedRef.current = true;
    if (initial.status === 'closed' || !initial.conversationId) return;

    fetch(`${BASE_URL}/api/rb/conversations/${initial.conversationId}/messages`, {
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load conversation'))))
      .then((data) => setMessages(data.messages ?? []))
      .catch(() => setError('Could not restore previous conversation'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startConversation = useCallback(async () => {
    if (startingConversationRef.current) return;
    startingConversationRef.current = true;
    try {
      const res = await fetch(`${BASE_URL}/api/rb/conversations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pageContext ? { pageContext } : {}),
      });
      if (!res.ok) throw new Error('Failed to start conversation');
      const data = await res.json();
      setConversationId(data.id);
      setStatus('open');
      persist({ status: 'open', conversationId: data.id });

      // Fetch any messages the backend seeded at creation time (e.g. a
      // "since you were last here" pending-signals message) so they're
      // visible immediately rather than only appearing after the user sends
      // their own first message.
      try {
        const messagesRes = await fetch(`${BASE_URL}/api/rb/conversations/${data.id}/messages`, {
          credentials: 'include',
        });
        if (messagesRes.ok) {
          const messagesData = await messagesRes.json();
          setMessages(messagesData.messages ?? []);
        }
      } catch {
        // Non-fatal — the conversation still works, just without any
        // seeded messages pre-populated.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start conversation');
    } finally {
      startingConversationRef.current = false;
    }
  }, [persist, pageContext]);

  const open = useCallback(() => {
    if (conversationId) {
      setStatus('open');
      persist({ status: 'open', conversationId });
      return;
    }
    if (startingConversationRef.current) return;
    void startConversation();
  }, [conversationId, persist, startConversation]);

  const minimize = useCallback(() => {
    setStatus('minimized');
    persist({ status: 'minimized', conversationId });
  }, [conversationId, persist]);

  const close = useCallback(() => {
    setStatus('closed');
    setConversationId(null);
    setMessages([]);
    lastSentPageContextRef.current = null;
    persist({ status: 'closed', conversationId: null });
  }, [persist]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!conversationId || isStreaming) return;
      setError(null);
      setIsStreaming(true);
      setStreamingContent('');

      const tempUserMsg: WidgetMessage = { id: Date.now(), role: 'user', content };
      setMessages((prev) => [...prev, tempUserMsg]);

      const controller = new AbortController();
      abortRef.current = controller;

      const includePageContext = !!pageContext && !pageContextEquals(pageContext, lastSentPageContextRef.current);

      try {
        const response = await fetch(`${BASE_URL}/api/rb/conversations/${conversationId}/messages`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(includePageContext ? { content, pageContext } : { content }),
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error('Failed to send message');
        if (includePageContext) lastSentPageContextRef.current = pageContext;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(trimmed.slice(6));
              if (event.type === 'delta' && event.content) {
                accumulated += event.content;
                setStreamingContent(accumulated);
              } else if (event.type === 'done') {
                setMessages((prev) => [...prev, { id: event.messageId || Date.now() + 1, role: 'assistant', content: accumulated }]);
                setStreamingContent('');
              } else if (event.type === 'error') {
                setError(event.error || 'AI service error');
              }
            } catch {
              // skip unparseable line
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError(err instanceof Error ? err.message : 'Failed to send message');
        }
      } finally {
        setIsStreaming(false);
        setStreamingContent('');
        abortRef.current = null;
      }
    },
    [conversationId, isStreaming, pageContext],
  );

  return { status, conversationId, messages, isStreaming, streamingContent, error, open, minimize, close, sendMessage };
}
